/**
 * CitationList — render de las citas usadas por el agente.
 *
 * Cada cita muestra filename + section_path + page + preview (chunk
 * truncado). Cuando `origin === "canon"` (Doc 22 Fase 8.k), la cita
 * viene de un documento oficial del proyecto — se renderiza un badge
 * "Oficial" para que el usuario distinga de un fragmento de fuente
 * subida. El "Abrir fuente" llega en AI-1.e cuando se conecte un
 * viewer de chunk en el editor (con highlight del char_range).
 */

import { useTranslation } from 'react-i18next'
import { FileText } from 'lucide-react'

import type { Citation } from '../../api/agents'

interface CitationListProps {
  citations: Citation[]
}

function citationLabel(c: Citation): string {
  if (c.filename) return c.filename
  if (c.source_id) return c.source_id.slice(0, 8)
  if (c.artifact_id) return c.artifact_id.slice(0, 8)
  return c.chunk_id.slice(0, 12)
}

export function CitationList({ citations }: CitationListProps) {
  const { t } = useTranslation('ai')

  if (citations.length === 0) {
    return (
      <div className="rounded-md border border-dashed border-line px-4 py-3 text-xs text-ink-subtle">
        {t('citations.no_citations')}
      </div>
    )
  }

  return (
    <div>
      <p className="mb-2 text-xs uppercase tracking-wider text-ink-subtle">
        {t('citations.heading')}
      </p>
      <ol className="space-y-2">
        {citations.map((c, idx) => {
          const isCanon = c.origin === 'canon'
          const sectionPath = c.section_path ?? []
          return (
            <li
              key={c.chunk_id}
              className="flex items-start gap-3 rounded-md border border-line px-3 py-2"
            >
              <span className="font-mono text-[10px] text-brand">
                [{idx + 1}]
              </span>
              <div className="min-w-0 flex-1 text-xs">
                <div className="flex items-center gap-2 text-ink">
                  <FileText className="h-3 w-3 shrink-0" aria-hidden="true" />
                  <span className="truncate font-medium">
                    {citationLabel(c)}
                  </span>
                  {isCanon && (
                    <span className="shrink-0 rounded-sm bg-official-soft px-1.5 py-0.5 font-mono text-[10px] uppercase tracking-wider text-official">
                      {t('citations.origin_official')}
                    </span>
                  )}
                  {c.page_number !== null && (
                    <span className="text-ink-subtle">
                      {t('citations.page_label', { page: c.page_number })}
                    </span>
                  )}
                </div>
                {sectionPath.length > 0 && (
                  <p className="mt-0.5 text-ink-subtle">
                    {sectionPath.join(' • ')}
                  </p>
                )}
                {c.preview && (
                  <p className="mt-1 line-clamp-2 text-ink-muted">
                    {c.preview}
                  </p>
                )}
              </div>
            </li>
          )
        })}
      </ol>
    </div>
  )
}
